// - - - - - - - - - -
// manualIndex.js — slug → file-path map for the in-app user manual.
//
// Language-aware from day 1 even though only English content exists today.
// When Spanish translations are authored, populate MANUAL_INDEX.es with
// the same shape as MANUAL_INDEX.en — no other code needs to change.
//
// Each entry is one renderable manual page:
//   - slug:  identifier used in selection state. Slugs containing "/"
//            (e.g. "time-travel/overview") are rendered in the nav under
//            an expandable group whose label comes from GROUP_LABELS.
//   - title: label shown in the left navigation (under the group, if any).
//   - path:  static-asset path relative to public/, fetched at runtime by
//            ManualContent. The build step (scripts/build-manual.js) copies
//            docs/user-manual/<lang>/ → public/manual/<lang>/, so e.g.
//            docs/user-manual/en/time-travel/overview.md is served at
//            /manual/en/time-travel/overview.md.

export const DEFAULT_LANGUAGE = "en";

// - - - - - - - - - -
// Group label registry. When a slug contains "/", the first segment is
// treated as a group identifier; this map provides the human-readable
// label rendered as the expandable parent in the nav. Unknown prefixes
// fall back to title-case of the segment.

export const GROUP_LABELS = {
  "time-travel": "Time Travel",
  "capacity-aware": "Capacity-Aware Scheduling",
  "lock-cells": "Locking a Day",
  compass: "Compass — Data Browser",
};

export const MANUAL_INDEX = {
  en: [
    {
      slug: "index",
      title: "Welcome",
      path: "/manual/en/index.md",
    },
    {
      slug: "getting-started",
      title: "Getting Started",
      path: "/manual/en/getting-started.md",
    },
    {
      slug: "time-travel/overview",
      title: "Overview",
      path: "/manual/en/time-travel/overview.md",
    },
    {
      slug: "time-travel/entering-and-exiting",
      title: "Entering and Exiting",
      path: "/manual/en/time-travel/entering-and-exiting.md",
    },
    {
      slug: "time-travel/the-sepia-banner",
      title: "The Sepia Banner",
      path: "/manual/en/time-travel/the-sepia-banner.md",
    },
    {
      slug: "time-travel/the-grid",
      title: "The Grid",
      path: "/manual/en/time-travel/the-grid.md",
    },
    {
      slug: "time-travel/history-modals",
      title: "History Modals",
      path: "/manual/en/time-travel/history-modals.md",
    },
    {
      slug: "time-travel/diff-pair-view",
      title: "Diff Pair View",
      path: "/manual/en/time-travel/diff-pair-view.md",
    },
    {
      slug: "time-travel/common-questions",
      title: "Common Questions",
      path: "/manual/en/time-travel/common-questions.md",
    },
    {
      slug: "time-travel/gotchas",
      title: "Gotchas",
      path: "/manual/en/time-travel/gotchas.md",
    },
    {
      slug: "time-travel/quick-reference",
      title: "Quick Reference",
      path: "/manual/en/time-travel/quick-reference.md",
    },
    {
      slug: "capacity-aware/overview",
      title: "Overview",
      path: "/manual/en/capacity-aware/overview.md",
    },
    {
      slug: "capacity-aware/next-day-openings",
      title: "Next Day Openings Dashboard",
      path: "/manual/en/capacity-aware/next-day-openings.md",
    },
    {
      slug: "capacity-aware/cell-highlights",
      title: "Cell Highlights",
      path: "/manual/en/capacity-aware/cell-highlights.md",
    },
    {
      slug: "capacity-aware/recommendations",
      title: "The Recommendations Popover",
      path: "/manual/en/capacity-aware/recommendations.md",
    },
    {
      slug: "capacity-aware/associated-stores",
      title: "Per-Installer Associated Stores",
      path: "/manual/en/capacity-aware/associated-stores.md",
    },
    {
      slug: "capacity-aware/decision-rules",
      title: "Decision Rules Deep-Dive",
      path: "/manual/en/capacity-aware/decision-rules.md",
    },
    {
      slug: "capacity-aware/common-questions",
      title: "Common Questions",
      path: "/manual/en/capacity-aware/common-questions.md",
    },
    {
      slug: "capacity-aware/quick-reference",
      title: "Quick Reference",
      path: "/manual/en/capacity-aware/quick-reference.md",
    },
    {
      slug: "lock-cells/overview",
      title: "Overview",
      path: "/manual/en/lock-cells/overview.md",
    },
    {
      slug: "lock-cells/locking-a-day",
      title: "Locking and Unlocking a Day",
      path: "/manual/en/lock-cells/locking-a-day.md",
    },
    {
      slug: "lock-cells/border-colors-and-icon",
      title: "Border Colors and the Lock Icon",
      path: "/manual/en/lock-cells/border-colors-and-icon.md",
    },
    {
      slug: "lock-cells/drops-and-drag-off",
      title: "Drops and Drag-Off",
      path: "/manual/en/lock-cells/drops-and-drag-off.md",
    },
    {
      slug: "lock-cells/common-questions",
      title: "Common Questions",
      path: "/manual/en/lock-cells/common-questions.md",
    },
    {
      slug: "compass/overview",
      title: "Overview",
      path: "/manual/en/compass/overview.md",
    },
    {
      slug: "compass/opening-and-access",
      title: "Opening Compass & Access",
      path: "/manual/en/compass/opening-and-access.md",
    },
    {
      slug: "compass/backends-and-environments",
      title: "Backends & Environments",
      path: "/manual/en/compass/backends-and-environments.md",
    },
    {
      slug: "compass/browsing-and-paging",
      title: "Browsing & Paging",
      path: "/manual/en/compass/browsing-and-paging.md",
    },
    {
      slug: "compass/searching-and-filtering",
      title: "Searching & Filtering",
      path: "/manual/en/compass/searching-and-filtering.md",
    },
    {
      slug: "compass/common-questions",
      title: "Common Questions",
      path: "/manual/en/compass/common-questions.md",
    },
  ],
  es: [],
};

// - - - - - - - - - -
// getManualPages(language) — returns the array of pages for a language,
// falling back to DEFAULT_LANGUAGE if the requested language has no
// content yet (e.g. early-stage Spanish).

export function getManualPages(language) {
  const pages = MANUAL_INDEX[language];
  if (Array.isArray(pages) && pages.length > 0) {
    return pages;
  }
  return MANUAL_INDEX[DEFAULT_LANGUAGE];
}

// - - - - - - - - - -
// findManualPage(language, slug) — looks up a single page; returns
// undefined if not found. Centralized here so callers don't have to
// know the index shape.

export function findManualPage(language, slug) {
  const pages = getManualPages(language);
  return pages.find((page) => page.slug === slug);
}

// - - - - - - - - - -
// resolveInternalSlug(href, pages) — maps a markdown link's href to a
// known page slug, or returns null if the href is external or doesn't
// match any page. Authors write inter-page links in the natural form
// ("getting-started.md", "./time-travel/overview.md") and the rendered
// custom <a> uses this to swap the right pane instead of navigating
// the browser (plan gotcha #8).
//
// Pure function — extracted so the link-routing logic can be tested
// directly and shared between production code and tests, matching the
// project's pattern for behavior-decision helpers.

export function resolveInternalSlug(href, pages) {
  if (typeof href !== "string" || href.length === 0) {
    return null;
  }
  // External links keep their default <a> behavior.
  if (/^[a-z][a-z0-9+\-.]*:/i.test(href) || href.startsWith("//")) {
    return null;
  }
  // Bare anchor links (#section) are not page navigations.
  if (href.startsWith("#")) {
    return null;
  }
  // Drop a query string or fragment if present, then leading "./" / "/"
  // and a trailing ".md" so the natural author-form matches our slug.
  const stripped = href.split(/[?#]/, 1)[0];
  let slug = stripped.replace(/^\.\//, "").replace(/^\/+/, "");
  slug = slug.replace(/\.md$/i, "");
  if (!Array.isArray(pages)) {
    return null;
  }
  const match = pages.find((page) => page.slug === slug);
  return match ? match.slug : null;
}

// - - - - - - - - - -
// filterManualPages(pages, hiddenGroupSlugs) — returns the page list
// with any page whose slug belongs to a hidden group removed. Pages
// without a "/" in their slug are top-level and are never filtered.
//
// Used to gate access-restricted chapters: when the logged-in user
// does not carry `Users.timeTravelAccess === true`, HelpModal passes
// `["time-travel"]` so the entire Time Travel chapter disappears
// from the manual nav (matches the same gate that hides the 🕒
// button in the header — `canAccessTimeMachine`).
//
// Pure function — same input always returns the same output.

export function filterManualPages(pages, hiddenGroupSlugs) {
  if (!Array.isArray(pages)) {
    return [];
  }
  if (!Array.isArray(hiddenGroupSlugs) || hiddenGroupSlugs.length === 0) {
    return pages;
  }
  const hiddenSet = new Set(hiddenGroupSlugs);
  return pages.filter((page) => {
    if (!page || typeof page.slug !== "string") {
      return false;
    }
    const sepIndex = page.slug.indexOf("/");
    if (sepIndex === -1) {
      return true; // top-level page — never filtered.
    }
    const groupSlug = page.slug.slice(0, sepIndex);
    return !hiddenSet.has(groupSlug);
  });
}

// - - - - - - - - - -
// preprocessAccessGates(markdown, hiddenGroupSlugs) — strips access-
// gated sections from a markdown string before it's rendered.
//
// Authors mark gated content with HTML-comment fences:
//
//     <!--access:time-travel-->
//     - **🕒 Time Travel** — opens the Time Machine, lets you ...
//     <!--/access:time-travel-->
//
// When the user's hiddenGroupSlugs contains "time-travel", the entire
// fenced block (markers + content) is removed. When access is granted,
// only the markers are removed and the content stays.
//
// Authors can nest different groups; each fence pair is matched by
// the group name in its opening tag. Comments without matching closing
// tag are left untouched (defensive — better to leak a marker into
// rendered output than to silently swallow body content).
//
// Pure function — same input always returns the same output. Used by
// ManualContent before passing markdown to react-markdown.

export function preprocessAccessGates(markdown, hiddenGroupSlugs) {
  if (typeof markdown !== "string") {
    return "";
  }
  const hiddenSet =
    Array.isArray(hiddenGroupSlugs) && hiddenGroupSlugs.length > 0
      ? new Set(hiddenGroupSlugs)
      : null;
  // The regex matches:
  //   <!--access:GROUP--> ...content... <!--/access:GROUP-->
  // Tolerant of whitespace inside the markers. Group name is captured
  // and back-referenced in the closing tag so we don't accidentally
  // match an opening of one group with the close of a different one.
  const fenceRegex =
    /<!--\s*access:([\w-]+)\s*-->([\s\S]*?)<!--\s*\/access:\1\s*-->/g;
  return markdown.replace(fenceRegex, (_match, group, content) => {
    if (hiddenSet && hiddenSet.has(group)) {
      return "";
    }
    return content;
  });
}

// - - - - - - - - - -
// titleCaseGroupSlug(slug) — fallback formatter for a group prefix that
// has no entry in GROUP_LABELS. Splits on hyphens, capitalizes each
// word: "time-travel" → "Time Travel", "customer-portal" → "Customer
// Portal". Visible to users only when authors add nested slugs without
// updating GROUP_LABELS — a soft default.

function titleCaseGroupSlug(slug) {
  return slug
    .split("-")
    .map((segment) =>
      segment.length > 0
        ? segment[0].toUpperCase() + segment.slice(1)
        : segment
    )
    .join(" ");
}

// - - - - - - - - - -
// buildNavTree(pages) — pure function. Converts a flat MANUAL_INDEX
// array into a mixed list of page nodes and group nodes, preserving the
// original page order. Top-level pages (slugs without "/") render as
// flat entries in the nav. Pages whose slug is "<group>/<rest>" are
// collected under a group node whose label is GROUP_LABELS[group] (or
// title-cased fallback). Group order in the output matches the order
// the first page of each group appears in the input.
//
// Pure for testability — same input always returns the same tree
// shape; ManualNav consumes this and adds the React rendering /
// expand-collapse state on top.

export function buildNavTree(pages) {
  if (!Array.isArray(pages)) {
    return [];
  }
  const tree = [];
  const groupIndexBySlug = new Map();
  for (const page of pages) {
    if (!page || typeof page.slug !== "string") {
      continue;
    }
    const sepIndex = page.slug.indexOf("/");
    if (sepIndex === -1) {
      tree.push({ type: "page", page });
      continue;
    }
    const groupSlug = page.slug.slice(0, sepIndex);
    if (!groupIndexBySlug.has(groupSlug)) {
      const groupNode = {
        type: "group",
        slug: groupSlug,
        label:
          Object.prototype.hasOwnProperty.call(GROUP_LABELS, groupSlug)
            ? GROUP_LABELS[groupSlug]
            : titleCaseGroupSlug(groupSlug),
        pages: [],
      };
      groupIndexBySlug.set(groupSlug, tree.length);
      tree.push(groupNode);
    }
    tree[groupIndexBySlug.get(groupSlug)].pages.push(page);
  }
  return tree;
}
